import './MerchStatistics.css';
import { compact } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../../components/Button/Button';
import { Card } from '../../../components/Card/Card';
import { Container } from '../../../components/Container/Container';
import { FilterBar } from '../../../components/FilterBar/FilterBar';
import { FormMultiSearch } from '../../../components/Form/AsyncSearch/FormMultiSearch';
import { Icon } from '../../../components/Icon/Icon';
import { ColumnContent, SortableList } from '../../../components/SortableList/SortableList';
import { t } from '../../../locales/translate';
import { serializeQuery } from '../../../model/filter';
import { useQueryAsState } from '../../../model/history';
import { getProductName, Product } from '../../../model/product';
import {
  getMerchStatisticsVariants,
  MerchStatsVariant,
  parseStatsParams,
  setBackgroundByProductId,
} from '../../../model/merchStatistics';
import { FormMultiSelect } from '../../../components/Form/Select/FormMultiSelect';
import { useTask } from '../../../model/data';
import { runStatVariantsExport } from '../../../model/exports';
import { FormCheckbox } from '../../../components/Form/Checkbox/FormCheckbox';
import { Link } from 'react-router-dom';
import { productPath } from '../../../routes';
import { useMeta } from '../../../model/meta';
import { getLast30Days } from '../../../util/datetime';
import { ListSearch } from '../../../components/ListSearch/ListSearch';

const PRODUCT_STATUSES = [
  { id: 'draft', label: t.model.status.draft },
  { id: 'in_review', label: t.model.status.inReview },
  { id: 'on_sale', label: t.model.status.onSale },
  { id: 'stopped', label: t.model.status.stopped },
];

export function MerchStatistics(): JSX.Element {
  useMeta({ breadcrumbs: [{ title: t.model.product.merchandise }, { title: t.model.merchStatistics._name }] });

  const [params, setParams] = useQueryAsState();
  const fetchId = useRef<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [data, setData] = useState<{
    endCursor?: string;
    hasNextPage?: boolean;
    variants: MerchStatsVariant[];
  }>();

  async function loadProductVariants(reset?: boolean): Promise<void> {
    setLoading(true);
    const stringParams = {
      ...params,
      terms: serializeQuery(params.query),
    };
    const paramsId = Object.values(stringParams).filter(Boolean).join('-');
    fetchId.current = paramsId;

    if (reset) setData({ endCursor: undefined, hasNextPage: undefined, variants: [] });

    try {
      const resp = await getMerchStatisticsVariants({
        ...parseStatsParams(stringParams),
        after: reset ? null : (data?.endCursor ?? null),
      });

      if (paramsId === fetchId.current) {
        setData((state) => {
          return {
            endCursor: resp.endCursor ?? undefined,
            hasNextPage: resp.hasNextPage,
            variants: reset ? resp.variants : [...(state?.variants || []), ...resp.variants],
          };
        });
      }
    } catch (error) {
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProductVariants(true);
  }, [JSON.stringify(params)]);

  useEffect(() => {
    if (!params.startDate && !params.endDate) {
      const timeframe = getLast30Days();
      setParams({ timeframe });
    }
  }, []);

  // Handle Export
  const downloadCsvTask = useTask({ handleError: true });
  async function handleDownload(): Promise<void> {
    const parsedParams = parseStatsParams({
      ...params,
      terms: serializeQuery(params.query),
    });

    downloadCsvTask.run(runStatVariantsExport({ ...parsedParams, productType: 'MerchProduct' }));
  }

  const formattedData = setBackgroundByProductId(data?.variants);

  const statisticsColumns: Array<ColumnContent<MerchStatsVariant & { className?: string }>> = compact(
    Object.keys(data?.variants[0]?.statistics || {}).map((item) => {
      if (item === '__typename') return undefined;

      return {
        label: t.model.merchStatistics[item].short,
        dataKey: 'statistics',
        keyOverride: `statistics.${item}`,
        align: 'right',
        tooltip: {
          content: (
            <>
              <strong>{t.model.merchStatistics[item].title}</strong>
              <p>{t.model.merchStatistics[item].description}</p>
            </>
          ),
          position: ['availableStock', 'reserved'].includes(item) ? 'top-end' : undefined,
        },
        cellRenderer: (row) => row.statistics?.[item],
      };
    })
  );

  return (
    <Container className="merch-statistics">
      <Card
        title={t.model.merchStatistics.productVariants}
        elevation={2}
        fullbleed
        actions={
          <div className="products__actions">
            <Icon
              onClick={handleDownload}
              loading={downloadCsvTask.loading}
              name="download"
              tooltip={t.actions.downloadAsCSV}
            />
          </div>
        }
        footer={
          data?.hasNextPage && (
            <Button
              loading={!!data?.variants.length && loading}
              title="load more"
              onClick={() => loadProductVariants()}
            />
          )
        }
      >
        <SortableList
          condensed
          error={error}
          items={formattedData}
          loading={!data?.variants.length && loading}
          actions={
            <FilterBar
              disableDefaults
              filters={['query', 'timeframe']}
              filterOrder={{
                query: 1,
                timeframe: 3,
              }}
              extraFilters={[
                {
                  key: 'categories',
                  order: 2,
                  renderer: (value, onChange) => {
                    return (
                      <FormMultiSearch
                        icon="folder"
                        className="merch-statistics__category-filter"
                        value={value ? value.split('*').filter(Boolean) : undefined}
                        searchIn={['category']}
                        label={t.model.category._plural}
                        onChange={(v) => onChange(v?.filter(Boolean).join('*'))}
                      />
                    );
                  },
                },
                {
                  key: 'productStatuses',
                  order: 4,
                  renderer: (value, onChange) => {
                    return (
                      <FormMultiSelect
                        className="merch-statistics__status-filter"
                        value={value ? value.split('*').filter(Boolean) : undefined}
                        onChange={(v) => {
                          onChange(v?.filter(Boolean).join('*'));
                        }}
                        label={t.model.merchStatistics.productStatus}
                        options={PRODUCT_STATUSES}
                      />
                    );
                  },
                },
                {
                  key: 'onlyActive',
                  order: 5,
                  renderer: (value, onChange) => {
                    return (
                      <FormCheckbox
                        label={t.model.merchStatistics.displayOnlyActive}
                        value={value === 'true' ? true : false}
                        onChange={(v) => onChange(v ? 'true' : 'false')}
                      />
                    );
                  },
                },
                {
                  key: 'skuList',
                  order: 6,
                  renderer: (value, onChange) => (
                    <ListSearch
                      iconName="shirt"
                      negation={false}
                      query={compact((value || '').split('*')).map((e) => ({ label: e, exclude: false }))}
                      onChange={(query) => onChange(query.map((e) => e.label).join('*'))}
                      size="large"
                    />
                  ),
                },
              ]}
            />
          }
          columns={[
            {
              label: t.model.product.sku,
              dataKey: 'sku',
            },
            {
              label: t.model.product._name,
              dataKey: 'product',
              width: '100%',
              cellRenderer: (row) => {
                const productName = [getProductName(row.product), row.description].filter(Boolean).join(' -- ');

                if (row.product) {
                  return <Link to={productPath(row.product as Product)}>{productName}</Link>;
                }
                return productName;
              },
            },
            ...statisticsColumns,
          ]}
        />
      </Card>
    </Container>
  );
}
